import React from 'react';
import PropTypes from 'prop-types';
import { Typography, RadioGroup } from '@telus-uds/components-web';
import { BoldFieldLabel, FieldWrapper } from './css';
import Note from '../../note';
import { fieldDoesCustomerSiteHaveAnExistingSecuritySystem } from '../../utils/constant';
import FieldGuide from './common/fieldGuide';

const FieldRadio = ({ data }) => {
  const {
    ref,
    name,
    label,
    options,
    required,
    onChange,
    note,
    readonly,
    value: fieldValue,
    disabled,
    tooltip,
    noteAppearsBelowField,
    isShowNoteBackground,
    isItalicNote
  } = data;

  const isDisabled = readonly || disabled;
  // UDS uses the item id as the DOM input id / label[for]; scope it to this
  // group so groups sharing option values (Yes/No, Oui/Non) stay independent.
  const toItemId = value => `${name}__${value}`;
  const items =
    options?.map(({ fields: { label: itemLabel, value, disabledOption } }) => ({
      label: itemLabel,
      id: toItemId(value),
      value,
      ...(disabledOption && { inactive: true })
    })) ?? [];

  const handleChange = selectedId => {
    if (onChange) {
      const selected = items.find(item => item.id === selectedId);
      onChange({
        target: { name, value: selected ? selected.value : selectedId }
      });
    }
  };

  return (
    <FieldWrapper>
      {name === fieldDoesCustomerSiteHaveAnExistingSecuritySystem ? (
        <div
          className="frg-body-font-14px"
          style={{ display: 'inline-flex' }}
          dangerouslySetInnerHTML={{ __html: label.split(',').join(`<br />`) }}
        />
      ) : (
        <div
          className="frg-body-font-14px"
          style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <BoldFieldLabel>
            <Typography block heading="h4" variant={{ size: 'h4', bold: true }}>
              {label}
              {required && <span style={{ marginLeft: '0.125rem' }}>*</span>}
              {tooltip && (
                <span
                  style={{
                    marginLeft: '0.25rem',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <FieldGuide tooltip={tooltip} />
                </span>
              )}
            </Typography>
          </BoldFieldLabel>
        </div>
      )}
      {note && note.content && !noteAppearsBelowField && (
        <Note
          type="richText"
          data={note}
          isShowNoteBackground={isShowNoteBackground}
          isItalicNote={isItalicNote}
        />
      )}
      <div id="fieldRadio">
        <RadioGroup
          ref={ref}
          legend={null}
          items={items}
          checkedId={
            fieldValue === undefined || fieldValue === null || fieldValue === ''
              ? null
              : toItemId(fieldValue)
          }
          onChange={handleChange}
          name={name}
          readOnly={readonly}
          inactive={isDisabled}
        />
      </div>
      {note && note.content && noteAppearsBelowField && (
        <Note
          type="richText"
          data={note}
          isShowNoteBackground={isShowNoteBackground}
        />
      )}
    </FieldWrapper>
  );
};

FieldRadio.propTypes = {
  data: PropTypes.shape({
    ref: PropTypes.object,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    readonly: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    tooltip: PropTypes.string.isRequired,
    noteAppearsBelowField: PropTypes.bool.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          value: PropTypes.string.isRequired
        })
      })
    ).isRequired,
    isShowNoteBackground: PropTypes.bool
  }).isRequired
};

export default FieldRadio;
